const ALLOWED_RESPONSE_STATUS_CODES = [200];

export type RequestResponse<U extends Record<string, unknown> | unknown[]> =
	| {
			statusCode: number | null;
			success: false;
			error: any;
			traceId: string | null;
	  }
	| {
			statusCode: number;
			success: true;
			data: U;
			traceId: string | null;
	  };

export class NetworkError extends Error {
	private readonly statusCode: number;

	constructor(statusCode: number, error: any) {
		super(error);
		this.statusCode = statusCode;
	}

	getStatusCode(): number {
		return this.statusCode;
	}
}

/**
 * The service's own error payload, or `null` when the body is not JSON.
 *
 * Read defensively rather than optimistically, because the status code is what
 * callers branch on and parsing used to be able to destroy it: a body that
 * would not parse threw out of the response path entirely and was reported as
 * a `500`, whatever the response actually said.
 *
 * A non-JSON error body is not an edge case. An edge or gateway commonly
 * answers an API error with a document: CloudFront, for one, can be configured
 * with a custom error response that keeps the status and replaces the body
 * with a static HTML page, which it applies per distribution rather than per
 * cache behaviour.
 *
 * `null` rather than the raw text on purpose. Callers treat a string `error` as
 * a message worth showing, and an HTML page is not a message.
 */
const readErrorBody = async (response: Response): Promise<unknown> => {
	try {
		return await response.json();
	} catch {
		return null;
	}
};

export async function request<T extends Record<string, unknown> | unknown[]>(
	method: string,
	url: string,
	data?: any,
	headers?: HeadersInit,
	statuses: number[] = ALLOWED_RESPONSE_STATUS_CODES,
	controller?: AbortController,
	noCredentials: boolean = false,
): Promise<RequestResponse<T>> {
	const requestConfig: RequestInit = {
		method,
		credentials: (noCredentials ? 'omit' : 'include') satisfies RequestCredentials,
		headers: {
			Accept: 'application/json',
			'Cache-Control': 'no-cache',
			'Content-Type': 'application/json',
			...headers,
		},
		...(data ? { body: JSON.stringify(data) } : undefined),
		...(controller ? { signal: controller.signal } : undefined),
	};

	try {
		const response = await fetch(url, requestConfig);
		if (response.ok || statuses?.includes(response.status)) {
			return {
				statusCode: response.status,
				success: true,
				data: await response.json(),
				traceId: getTraceId(response),
			};
		}
		return {
			statusCode: response.status,
			success: false,
			error: await readErrorBody(response),
			traceId: getTraceId(response),
		};
	} catch (error) {
		if (controller?.signal.aborted) {
			return {
				statusCode: null,
				success: false,
				error: controller?.signal.reason ?? 'Request aborted',
				traceId: null,
			};
		}
		if (error instanceof NetworkError) {
			return {
				statusCode: error.getStatusCode(),
				success: false,
				error: error.message,
				traceId: null,
			};
		}
		return {
			statusCode: 500,
			success: false,
			error: error instanceof Error ? error.message : JSON.stringify(error),
			traceId: null,
		};
	}
}

export const requestWithAbort = <T extends Record<string, unknown> | unknown[]>(
	method: string,
	url: string,
	data?: any,
	headers?: HeadersInit,
	statuses: number[] = ALLOWED_RESPONSE_STATUS_CODES,
	noCredentials: boolean = false,
): { controller: AbortController; execute: Promise<RequestResponse<T>> } => {
	const controller = new AbortController();
	const response = request<T>(method, url, data, headers, statuses, controller, noCredentials);
	return { controller, execute: response };
};

const getTraceId = (response: Response): string | null => {
	return (
		response.headers.get('atl-traceid') ||
		response.headers.get('X-Trace-Id') ||
		response.headers.get('atl-request-id') ||
		null
	);
};
